import {useState, useEffect} from "react"
import {useTranslation} from "react-i18next"
import {useLocation, useNavigate, useSearchParams} from "react-router-dom"
import {Heading} from "@/components/tailwind/heading"
import {Text} from "@/components/tailwind/text"
import {Divider} from "@/components/tailwind/divider"
import {Button} from "@/components/tailwind/button"
import {
    validateEmailVerificationToken,
    confirmEmailVerification,
    EmailVerificationUserMismatchError,
} from "@/services/email-verification-services"
import ValidationAlert from "@/components/validation-alert"
import {useAuth} from "@/context/use-auth"
import {useToast} from "@/hooks/use-toast"

type ViewState =
    | "validating"
    | "invalid-token"
    | "login-required"
    | "user-mismatch"
    | "confirming"
    | "error"

export default function EmailVerificationConfirmPage() {
    const {t} = useTranslation("common")
    const {isLoggedIn, token: jwt, logout} = useAuth()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const location = useLocation()
    const {toast} = useToast()
    const token = searchParams.get("token") || ""

    const [view, setView] = useState<ViewState>("validating")

    useEffect(() => {
        if (!token) {
            setView("invalid-token")
            return
        }

        let cancelled = false
        validateEmailVerificationToken(token)
            .then(result => {
                if (cancelled) return
                if (!result.valid) {
                    setView("invalid-token")
                    return
                }
                if (!isLoggedIn() || !jwt) {
                    setView("login-required")
                    return
                }
                setView("confirming")
                confirmEmailVerification(token, jwt)
                    .then(() => {
                        if (cancelled) return
                        toast({
                            title: t("emailVerification.toastTitle"),
                            description: t("emailVerification.success"),
                            variant: "default",
                        })
                        navigate("/dashboard")
                    })
                    .catch(e => {
                        if (cancelled) return
                        if (e instanceof EmailVerificationUserMismatchError) {
                            setView("user-mismatch")
                        } else {
                            setView("error")
                        }
                    })
            })
            .catch(() => {
                if (cancelled) return
                setView("invalid-token")
            })

        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    function goToLoginWithReturn() {
        navigate("/login", {
            state: {
                from: {
                    pathname: location.pathname,
                    search: location.search,
                },
            },
        })
    }

    function logoutAndReturn() {
        logout()
        goToLoginWithReturn()
    }

    if (view === "validating" || view === "confirming") {
        return (
            <div className="mx-auto max-w-4xl" data-testid="email-verification-validating">
                <Heading level={2}>{t("emailVerification.title")}</Heading>
                <Divider className="my-10 mt-6"/>
                <Text role="status" aria-busy="true">
                    {t("emailVerification.validating")}
                </Text>
            </div>
        )
    }

    if (view === "invalid-token") {
        return (
            <div className="mx-auto max-w-4xl">
                <Heading level={2}>{t("emailVerification.title")}</Heading>
                <Divider className="my-10 mt-6"/>
                <div className="mb-6" data-testid="email-verification-invalid-token">
                    <ValidationAlert message={t("emailVerification.errors.invalidToken")}/>
                </div>
            </div>
        )
    }

    if (view === "login-required") {
        return (
            <div className="mx-auto max-w-4xl" data-testid="email-verification-login-required">
                <Heading level={2}>{t("emailVerification.title")}</Heading>
                <Divider className="my-10 mt-6"/>
                <div className="rounded-md bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4 mb-6">
                    <Text className="font-medium text-blue-700 dark:text-blue-300">
                        {t("emailVerification.loginRequired.heading")}
                    </Text>
                    <Text className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                        {t("emailVerification.loginRequired.description")}
                    </Text>
                </div>
                <Button
                    onClick={goToLoginWithReturn}
                    data-testid="email-verification-login-button"
                >
                    {t("emailVerification.loginRequired.action")}
                </Button>
            </div>
        )
    }

    if (view === "user-mismatch") {
        return (
            <div className="mx-auto max-w-4xl" data-testid="email-verification-user-mismatch">
                <Heading level={2}>{t("emailVerification.title")}</Heading>
                <Divider className="my-10 mt-6"/>
                <div className="rounded-md bg-amber-50 dark:bg-amber-950 border-l-4 border-amber-400 dark:border-amber-500 p-4 mb-6">
                    <Text className="font-medium text-amber-700 dark:text-amber-300">
                        {t("emailVerification.userMismatch.heading")}
                    </Text>
                    <Text className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                        {t("emailVerification.userMismatch.description")}
                    </Text>
                </div>
                <Button
                    onClick={logoutAndReturn}
                    data-testid="email-verification-logout-button"
                >
                    {t("emailVerification.userMismatch.action")}
                </Button>
            </div>
        )
    }

    return (
        <div className="mx-auto max-w-4xl" data-testid="email-verification-error">
            <Heading level={2}>{t("emailVerification.title")}</Heading>
            <Divider className="my-10 mt-6"/>
            <ValidationAlert message={t("emailVerification.errors.serverError")}/>
        </div>
    )
}
